/**
 * CustomerInvoices — ledger of platform revenue invoices (Careem, Noon,
 * Talabat, Keeta, etc.) with a per-invoice breakdown of positive revenue
 * lines and negative deductions. Computes Net = Revenue − Deductions
 * + VAT so finance can tally the actual AED credited to the bank.
 *
 * This is NOT an invoice-generation module — it only captures invoices
 * already received from the platforms.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, FileText, X, Download } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INVOICES_API = `${API}/customer-invoices`;
const getAuthHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmtAED = (n) => `AED ${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const toDDMMYYYY = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
};
const MONTHS = [
  { v: 1, l: 'January' }, { v: 2, l: 'February' }, { v: 3, l: 'March' },
  { v: 4, l: 'April' }, { v: 5, l: 'May' }, { v: 6, l: 'June' },
  { v: 7, l: 'July' }, { v: 8, l: 'August' }, { v: 9, l: 'September' },
  { v: 10, l: 'October' }, { v: 11, l: 'November' }, { v: 12, l: 'December' },
];
const NOW = new Date();

const emptyForm = () => ({
  platform_id: '', platform_name: '',
  work_start_date: '', work_end_date: '',
  month: NOW.getMonth() + 1, year: NOW.getFullYear(),
  zoho_invoice_number: '',
  invoice_date: '',
  total_amount_excl_vat: 0,
  vat_amount: 0,
  breakdown: [],
  notes: '',
});

const emptyRow = () => ({ category: '', description: '', amount: 0, vat: 0 });

const CustomerInvoices = () => {
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [grandTotals, setGrandTotals] = useState({ total_revenue: 0, total_deductions: 0, total_vat: 0, net_amount_in_bank: 0 });
  const [platforms, setPlatforms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filter, setFilter] = useState({ platform_name: '', month: '', year: '' });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (filter.platform_name) q.set('platform_name', filter.platform_name);
      if (filter.month) q.set('month', filter.month);
      if (filter.year) q.set('year', filter.year);
      const res = await axios.get(`${INVOICES_API}?${q.toString()}`, { headers: getAuthHeader() });
      setInvoices(res.data.invoices || []);
      setGrandTotals(res.data.grand_totals || {});
    } catch (e) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const fetchMeta = async () => {
    try {
      const [plats, cats] = await Promise.all([
        axios.get(`${INVOICES_API}/platforms`, { headers: getAuthHeader() }),
        axios.get(`${INVOICES_API}/categories`, { headers: getAuthHeader() }),
      ]);
      setPlatforms(plats.data.platforms || []);
      setCategories(cats.data.categories || []);
    } catch (e) { /* non-fatal */ }
  };

  useEffect(() => { fetchMeta(); }, []);
  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [filter]);

  // ---- Live client-side totals (mirror backend logic) ----
  const liveTotals = useMemo(() => {
    let rev = 0, ded = 0, vat = 0;
    (form.breakdown || []).forEach((r) => {
      const a = Number(r.amount) || 0;
      const v = Number(r.vat) || 0;
      if (a >= 0) rev += a; else ded += Math.abs(a);
      vat += v;
    });
    return {
      total_revenue: rev,
      total_deductions: ded,
      total_vat: vat,
      net_amount_in_bank: rev - ded + vat,
    };
  }, [form.breakdown]);

  const openCreate = () => { setEditingId(null); setForm(emptyForm()); setDialogOpen(true); };
  const openEdit = (inv) => {
    setEditingId(inv.id);
    setForm({
      platform_id: inv.platform_id || '',
      platform_name: inv.platform_name || '',
      work_start_date: inv.work_start_date || '',
      work_end_date: inv.work_end_date || '',
      month: inv.month, year: inv.year,
      zoho_invoice_number: inv.zoho_invoice_number || '',
      invoice_date: inv.invoice_date || '',
      total_amount_excl_vat: inv.total_amount_excl_vat || 0,
      vat_amount: inv.vat_amount || 0,
      breakdown: (inv.breakdown || []).map((r) => ({ ...r })),
      notes: inv.notes || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.platform_name) { toast.error('Please select a platform'); return; }
    if (!form.month || !form.year) { toast.error('Please select invoicing month & year'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        total_amount_excl_vat: Number(form.total_amount_excl_vat) || 0,
        vat_amount: Number(form.vat_amount) || 0,
        breakdown: (form.breakdown || []).map((r) => ({
          category: r.category || '',
          description: r.description || '',
          amount: Number(r.amount) || 0,
          vat: Number(r.vat) || 0,
        })),
      };
      if (editingId) {
        await axios.put(`${INVOICES_API}/${editingId}`, payload, { headers: getAuthHeader() });
        toast.success('Invoice updated');
      } else {
        await axios.post(`${INVOICES_API}`, payload, { headers: getAuthHeader() });
        toast.success('Invoice added');
      }
      setDialogOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save invoice');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.zoho_invoice_number || inv.id}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${INVOICES_API}/${inv.id}`, { headers: getAuthHeader() });
      toast.success('Invoice deleted');
      fetchData();
    } catch (e) {
      toast.error('Failed to delete invoice');
    }
  };

  // ---- CSV Export ----
  const exportCSV = () => {
    const rows = [
      ['Month', 'Year', 'Platform', 'Invoice No.', 'Invoice Date', 'Work Start', 'Work End', 'Total Revenue', 'Total Deductions', 'VAT', 'Net Amount in Bank'],
      ...invoices.map((i) => [
        MONTHS.find((m) => m.v === i.month)?.l || i.month,
        i.year,
        i.platform_name,
        i.zoho_invoice_number,
        toDDMMYYYY(i.invoice_date),
        toDDMMYYYY(i.work_start_date),
        toDDMMYYYY(i.work_end_date),
        i.computed_totals?.total_revenue ?? 0,
        i.computed_totals?.total_deductions ?? 0,
        i.computed_totals?.total_vat ?? 0,
        i.computed_totals?.net_amount_in_bank ?? 0,
      ]),
    ];
    const csv = rows.map((r) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `customer-invoices-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ---- Breakdown row helpers ----
  const addBreakdownRow = () => setForm({ ...form, breakdown: [...(form.breakdown || []), emptyRow()] });
  const removeBreakdownRow = (idx) => setForm({ ...form, breakdown: form.breakdown.filter((_, i) => i !== idx) });
  const updateBreakdownRow = (idx, patch) => {
    const next = [...form.breakdown];
    next[idx] = { ...next[idx], ...patch };
    setForm({ ...form, breakdown: next });
  };

  return (
    <div className="space-y-4" data-testid="customer-invoices-root">
      {/* KPI tiles (grand totals across filtered rows) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Total Revenue" value={grandTotals.total_revenue} tone="green" testid="kpi-total-revenue" />
        <Kpi label="Total Deductions" value={grandTotals.total_deductions} tone="red" testid="kpi-total-deductions" />
        <Kpi label="VAT" value={grandTotals.total_vat} tone="blue" testid="kpi-total-vat" />
        <Kpi label="Net Amount in Bank" value={grandTotals.net_amount_in_bank} tone="indigo" testid="kpi-net-amount" />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex flex-col gap-1">
          <Label className="text-xs text-slate-600">Platform</Label>
          <Select value={filter.platform_name || 'all'} onValueChange={(v) => setFilter({ ...filter, platform_name: v === 'all' ? '' : v })}>
            <SelectTrigger className="w-[180px]" data-testid="filter-platform"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All platforms</SelectItem>
              {platforms.map((p) => (<SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-xs text-slate-600">Month</Label>
          <Select value={filter.month ? String(filter.month) : 'all'} onValueChange={(v) => setFilter({ ...filter, month: v === 'all' ? '' : Number(v) })}>
            <SelectTrigger className="w-[140px]" data-testid="filter-month"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All months</SelectItem>
              {MONTHS.map((m) => (<SelectItem key={m.v} value={String(m.v)}>{m.l}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1">
          <Label className="text-xs text-slate-600">Year</Label>
          <Input className="w-[110px]" value={filter.year} onChange={(e) => setFilter({ ...filter, year: e.target.value ? Number(e.target.value) : '' })} placeholder="e.g. 2026" data-testid="filter-year" />
        </div>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={exportCSV} data-testid="export-invoices-csv"><Download className="w-4 h-4 mr-1" />Export CSV</Button>
          <Button onClick={openCreate} data-testid="add-invoice-details-btn"><Plus className="w-4 h-4 mr-1" />Add Invoice details</Button>
        </div>
      </div>

      {/* Log table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Month</TableHead>
                  <TableHead>Year</TableHead>
                  <TableHead>Platform</TableHead>
                  <TableHead>Zoho Invoice No.</TableHead>
                  <TableHead>Invoice Date</TableHead>
                  <TableHead className="text-right">Total Revenue</TableHead>
                  <TableHead className="text-right">Total Deductions</TableHead>
                  <TableHead className="text-right">VAT</TableHead>
                  <TableHead className="text-right">Net Amount in Bank</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={10} className="text-center py-6 text-slate-500">Loading…</TableCell></TableRow>
                ) : invoices.length === 0 ? (
                  <TableRow><TableCell colSpan={10} className="text-center py-10 text-slate-500">
                    <FileText className="w-6 h-6 inline-block mr-2 opacity-50" />
                    No invoices yet. Click <strong>Add Invoice details</strong> to record one.
                  </TableCell></TableRow>
                ) : (
                  invoices.map((inv) => (
                    <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                      <TableCell>{MONTHS.find((m) => m.v === inv.month)?.l || inv.month}</TableCell>
                      <TableCell>{inv.year}</TableCell>
                      <TableCell><span className="font-medium">{inv.platform_name}</span></TableCell>
                      <TableCell className="font-mono text-xs">{inv.zoho_invoice_number || '-'}</TableCell>
                      <TableCell>{toDDMMYYYY(inv.invoice_date)}</TableCell>
                      <TableCell className="text-right text-emerald-700 font-medium">{fmtAED(inv.computed_totals?.total_revenue)}</TableCell>
                      <TableCell className="text-right text-rose-700 font-medium">{fmtAED(inv.computed_totals?.total_deductions)}</TableCell>
                      <TableCell className="text-right text-blue-700">{fmtAED(inv.computed_totals?.total_vat)}</TableCell>
                      <TableCell className="text-right font-bold text-indigo-700">{fmtAED(inv.computed_totals?.net_amount_in_bank)}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(inv)} data-testid={`edit-invoice-${inv.id}`}><Pencil className="w-4 h-4" /></Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600 hover:text-rose-800" onClick={() => handleDelete(inv)} data-testid={`delete-invoice-${inv.id}`}><Trash2 className="w-4 h-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* ------ Add / Edit dialog ------ */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="invoice-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Invoice' : 'Add Invoice Details'}</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <div className="flex flex-col gap-1">
              <Label>Platform *</Label>
              <Select
                value={form.platform_name || ''}
                onValueChange={(v) => {
                  const p = platforms.find((pl) => pl.name === v);
                  setForm({ ...form, platform_name: v, platform_id: p?.id || '' });
                }}
              >
                <SelectTrigger data-testid="form-platform"><SelectValue placeholder="Select platform" /></SelectTrigger>
                <SelectContent>
                  {platforms.map((p) => (<SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <Label>Work Start Date</Label>
              <Input type="date" value={form.work_start_date} onChange={(e) => setForm({ ...form, work_start_date: e.target.value })} data-testid="form-work-start" />
            </div>
            <div className="flex flex-col gap-1">
              <Label>Work End Date</Label>
              <Input type="date" value={form.work_end_date} onChange={(e) => setForm({ ...form, work_end_date: e.target.value })} data-testid="form-work-end" />
            </div>

            <div className="flex flex-col gap-1">
              <Label>Invoicing Month *</Label>
              <Select value={String(form.month)} onValueChange={(v) => setForm({ ...form, month: Number(v) })}>
                <SelectTrigger data-testid="form-month"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MONTHS.map((m) => (<SelectItem key={m.v} value={String(m.v)}>{m.l}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1">
              <Label>Invoicing Year *</Label>
              <Input type="number" min="2020" max="2100" value={form.year} onChange={(e) => setForm({ ...form, year: Number(e.target.value) || NOW.getFullYear() })} data-testid="form-year" />
            </div>
            <div className="flex flex-col gap-1">
              <Label>Zoho Invoice No.</Label>
              <Input value={form.zoho_invoice_number} onChange={(e) => setForm({ ...form, zoho_invoice_number: e.target.value })} data-testid="form-zoho-no" />
            </div>

            <div className="flex flex-col gap-1">
              <Label>Invoice Date</Label>
              <Input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} data-testid="form-invoice-date" />
            </div>
            <div className="flex flex-col gap-1">
              <Label>Total Invoice Amount (Excl. VAT)</Label>
              <Input type="number" step="0.01" value={form.total_amount_excl_vat} onChange={(e) => setForm({ ...form, total_amount_excl_vat: e.target.value })} data-testid="form-total-excl-vat" />
            </div>
            <div className="flex flex-col gap-1">
              <Label>VAT Amount</Label>
              <Input type="number" step="0.01" value={form.vat_amount} onChange={(e) => setForm({ ...form, vat_amount: e.target.value })} data-testid="form-vat-amount" />
            </div>
          </div>

          {/* Breakdown */}
          <div className="mt-5">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-slate-800">Invoice Breakdown</h3>
              <Button size="sm" variant="outline" onClick={addBreakdownRow} data-testid="add-breakdown-row"><Plus className="w-3 h-3 mr-1" />Add Line</Button>
            </div>
            <div className="overflow-x-auto border rounded">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="min-w-[220px]">Category</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="w-[140px] text-right">Amount (AED)</TableHead>
                    <TableHead className="w-[120px] text-right">VAT</TableHead>
                    <TableHead className="w-[60px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(form.breakdown || []).length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-4 text-sm">No breakdown lines yet — click Add Line.</TableCell></TableRow>
                  ) : (form.breakdown || []).map((row, idx) => {
                    const amt = Number(row.amount) || 0;
                    return (
                      <TableRow key={idx} data-testid={`breakdown-row-${idx}`}>
                        <TableCell>
                          <Select value={row.category} onValueChange={(v) => updateBreakdownRow(idx, { category: v })}>
                            <SelectTrigger data-testid={`row-${idx}-category`}><SelectValue placeholder="Select category" /></SelectTrigger>
                            <SelectContent className="max-h-[300px]">
                              {categories.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Input value={row.description} onChange={(e) => updateBreakdownRow(idx, { description: e.target.value })} placeholder={row.category === 'Others' ? 'Describe this adjustment' : 'Optional'} data-testid={`row-${idx}-desc`} />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number" step="0.01" className={`text-right font-semibold ${amt > 0 ? 'text-emerald-700' : amt < 0 ? 'text-rose-700' : ''}`}
                            value={row.amount} onChange={(e) => updateBreakdownRow(idx, { amount: e.target.value })}
                            data-testid={`row-${idx}-amount`}
                          />
                        </TableCell>
                        <TableCell>
                          <Input type="number" step="0.01" className="text-right" value={row.vat} onChange={(e) => updateBreakdownRow(idx, { vat: e.target.value })} data-testid={`row-${idx}-vat`} />
                        </TableCell>
                        <TableCell>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600 hover:text-rose-800" onClick={() => removeBreakdownRow(idx)} data-testid={`row-${idx}-remove`}><X className="w-4 h-4" /></Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Live totals summary */}
          <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi label="Total Revenue" value={liveTotals.total_revenue} tone="green" testid="live-total-revenue" compact />
            <Kpi label="Total Deductions" value={liveTotals.total_deductions} tone="red" testid="live-total-deductions" compact />
            <Kpi label="VAT" value={liveTotals.total_vat} tone="blue" testid="live-total-vat" compact />
            <Kpi label="Net Amount in Bank" value={liveTotals.net_amount_in_bank} tone="indigo" testid="live-net-amount" compact />
          </div>

          {/* Notes */}
          <div className="mt-4 flex flex-col gap-1">
            <Label>Notes (optional)</Label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="form-notes" />
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="form-cancel">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="form-save">{saving ? 'Saving…' : (editingId ? 'Save Changes' : 'Save Invoice')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Kpi = ({ label, value, tone, testid, compact }) => {
  const bg = {
    green: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    red: 'bg-rose-50 border-rose-200 text-rose-800',
    blue: 'bg-blue-50 border-blue-200 text-blue-800',
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-900',
  }[tone] || 'bg-slate-50 border-slate-200 text-slate-800';
  return (
    <div className={`${bg} border rounded-lg px-4 ${compact ? 'py-2' : 'py-3'}`} data-testid={testid}>
      <div className="text-xs opacity-80">{label}</div>
      <div className={`font-bold ${compact ? 'text-base' : 'text-lg'}`}>{fmtAED(value)}</div>
    </div>
  );
};

export default CustomerInvoices;
